import { ROUTES } from '@app/navigation/registry';
import {
  Bell,
  Building2,
  CalendarRange,
  ChevronDown,
  Keyboard,
  LogOut,
  Menu,
  PanelRight,
  User,
  X,
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

import { useAuthStore } from '@stores/auth-store';
import { useUIStore } from '@stores/ui-store';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@shared/components/ui/dropdown-menu';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@shared/components/ui/tooltip';
import { cn } from '@shared/utils/cn';

import { useLogout } from '@features/auth/hooks/use-workshop-auth';

import styles from './ERPHeader.module.scss';

// ─── Component ────────────────────────────────────────────────────────────────

export function ERPHeader() {
  const sidebarCollapsed = useUIStore((s) => s.sidebarCollapsed);
  const toggleSidebar = useUIStore((s) => s.toggleSidebar);
  const toggleUtilityPanel = useUIStore((s) => s.toggleUtilityPanel);
  const utilityPanelOpen = useUIStore((s) => s.utilityPanelOpen);
  const utilityPanelTab = useUIStore((s) => s.utilityPanelTab);
  const notifications = useUIStore((s) => s.notifications);
  const unreadNotificationsCount = useUIStore((s) => s.unreadNotificationsCount);
  const user = useAuthStore((s) => s.user);
  const workshop = useAuthStore((s) => s.workshop);
  const logoutMutation = useLogout();
  const navigate = useNavigate();

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  const unreadCount =
    unreadNotificationsCount > 0 ? unreadNotificationsCount : notifications.length;

  // User initials for avatar fallback
  const initials =
    user?.name
      .split(' ')
      .map((n) => n[0])
      .slice(0, 2)
      .join('')
      .toUpperCase() ?? 'U';

  return (
    <header className={styles.header} role="banner" id="erp-header">
      {/* ── Left: Sidebar toggle ──────────────────────────────────────────── */}
      <div className={styles.left}>
        {/* Desktop collapse toggle */}
        <TooltipProvider delayDuration={400}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={cn(styles.iconBtn, styles.sidebarToggle)}
                onClick={toggleSidebar}
                aria-label="Toggle sidebar (Ctrl+B)"
              >
                {sidebarCollapsed ? (
                  <Menu size={18} aria-hidden="true" />
                ) : (
                  <X size={18} aria-hidden="true" />
                )}
              </button>
            </TooltipTrigger>
            <TooltipContent>Toggle sidebar (Ctrl+B)</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* ── Center: Context ──────────────────────────────── */}
      <div className={styles.center}>
        {/* Workshop / Branch / FY context chip */}
        {workshop ? (
          <div className={styles.contextChip} aria-label="Current workshop context">
            <span className={styles.contextIcon}>
              <Building2 size={12} />
            </span>
            <span className={styles.contextWorkshop}>{workshop.name}</span>
            {workshop.branchName && workshop.branchName !== workshop.name && (
              <>
                <span className={styles.contextSep}>·</span>
                <span className={styles.contextBranch}>{workshop.branchName}</span>
              </>
            )}
            {workshop.financialYearLabel && (
              <>
                <span className={styles.contextSep}>·</span>
                <span className={styles.contextFY}>
                  <CalendarRange size={11} aria-hidden="true" />
                  {workshop.financialYearLabel}
                </span>
              </>
            )}
          </div>
        ) : user?.businessName || user?.name ? (
          <div className={styles.contextChip} aria-label="Current workshop context">
            <span className={styles.contextIcon}>
              <Building2 size={12} />
            </span>
            <span className={styles.contextWorkshop}>{user?.businessName || user?.name}</span>
            {user?.code && (
              <>
                <span className={styles.contextSep}>·</span>
                <span className={styles.contextBranch}>{user.code}</span>
              </>
            )}
          </div>
        ) : null}
      </div>

      {/* ── Right: Actions ───────────────────────────────────────────────── */}
      <div className={styles.right}>
        {/* Notifications bell */}
        <TooltipProvider delayDuration={400}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={unreadCount > 0 ? styles.notifBtn : styles.iconBtn}
                onClick={() => toggleUtilityPanel('notifications')}
                aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
              >
                <Bell
                  size={15}
                  aria-hidden="true"
                  fill={unreadCount > 0 ? '#ffffff' : 'none'}
                  color={unreadCount > 0 ? '#ffffff' : 'currentColor'}
                />
                {unreadCount > 0 && <span className={styles.notifCount}>{unreadCount}</span>}
              </button>
            </TooltipTrigger>
            <TooltipContent>Notifications</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Utility panel toggle */}
        <TooltipProvider delayDuration={400}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                className={cn(
                  styles.iconBtn,
                  utilityPanelOpen && utilityPanelTab !== 'notifications' && styles.iconBtnActive,
                )}
                onClick={() => toggleUtilityPanel('shortcuts')}
                aria-label="Toggle quick panel"
                aria-expanded={utilityPanelOpen && utilityPanelTab !== 'notifications'}
              >
                <PanelRight size={16} aria-hidden="true" />
              </button>
            </TooltipTrigger>
            <TooltipContent>Quick panel</TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {/* Divider */}
        <div className={styles.divider} role="separator" aria-hidden="true" />

        {/* User dropdown */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={styles.userTrigger}
              aria-label={`User menu — ${user?.name ?? 'Account'}`}
            >
              <div className={styles.avatar} aria-hidden="true">
                {user?.avatarUrl ? (
                  <img src={user.avatarUrl} alt="" className={styles.avatarImg} />
                ) : (
                  <span className={styles.avatarInitials}>{initials}</span>
                )}
              </div>
              <div className={styles.userInfo}>
                <span className={styles.userName}>{user?.name ?? 'User'}</span>
                <span className={styles.userRole}>{user?.role ?? 'Staff'}</span>
              </div>
              <ChevronDown size={12} className={styles.userChevron} aria-hidden="true" />
            </button>
          </DropdownMenuTrigger>

          <DropdownMenuContent align="end" className={styles.userMenu} sideOffset={6}>
            <DropdownMenuLabel className={styles.userMenuLabel}>
              <div className={styles.userMenuName}>{user?.name}</div>
              <div className={styles.userMenuEmail}>{user?.email}</div>
            </DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className={styles.menuItem}
              onSelect={() => navigate(ROUTES.SETTINGS_PROFILE)}
            >
              <User size={14} aria-hidden="true" />
              <span>Business Profile</span>
            </DropdownMenuItem>
            <DropdownMenuItem
              className={styles.menuItem}
              onSelect={() => toggleUtilityPanel('shortcuts')}
            >
              <Keyboard size={14} aria-hidden="true" />
              <span>Keyboard Shortcuts</span>
              <kbd className={styles.menuKbd}>F1</kbd>
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuItem
              className={cn(styles.menuItem, styles.menuItemDanger)}
              onSelect={handleLogout}
            >
              <LogOut size={14} aria-hidden="true" />
              <span>Log out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  );
}
